import { useState, useMemo, useEffect } from 'react';
import EnhancedTable from '../../components/common/EnhancedTable';
import ExpandableText from '../../components/common/ExpandableText';
import type { SubLeaseFilter, SortConfig } from '../../types/lease';
import { format } from 'date-fns';
import SubLeaseFilters from '../../components/SubLeases/SubLeaseFilters';
import SubLeaseSortControls, { SubLeaseSortField } from '../../components/SubLeases/SubLeaseSortControls';
import { useDirectSubLeases } from '../../hooks/useDirectSubLeases';
import { formatTextWithLinks } from '../../utils/formatters';

export default function ReadOnlySubLeases() {
  const [searchQuery, setSearchQuery] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState<SubLeaseFilter>({
    holder: null,
    status: null,
    endDateFrom: null,
    endDateTo: null,
    leaseDealingNo: null
  });
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: 'lease_id',
    direction: 'asc'
  });

  // Use the direct hook to fetch and filter data
  const { data: subleases, isLoading, error } = useDirectSubLeases(
    filters,
    sortConfig,
    searchQuery
  );

  // Log data for debugging
  useEffect(() => {
    console.log('SubLeases component - subleases data:', subleases);
  }, [subleases]);

  // Extract unique holders for filter dropdowns
  const holders = useMemo(() => {
    const holdersSet = new Set<string>();

    subleases?.forEach(lease => {
      if (lease.holder_name && lease.holder_name !== 'N/A') {
        holdersSet.add(lease.holder_name);
      }
    });

    return Array.from(holdersSet).sort();
  }, [subleases]);

  const handleSort = (field: SubLeaseSortField) => {
    setSortConfig(current => ({
      field,
      direction: current.field === field && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleFilterChange = (newFilters: Partial<SubLeaseFilter>) => {
    setFilters(current => ({ ...current, ...newFilters }));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-800"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-red-500">
        <h2 className="text-xl font-bold mb-2">Error loading sublease data:</h2>
        <pre className="bg-red-50 p-4 rounded overflow-auto">{error.message}</pre>
      </div>
    );
  }

  return (
    <div className="p-4 h-[calc(100vh-6rem)] overflow-y-auto">
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold">Sub-Leases</h1>
        </div>

        <div className="flex items-center gap-2">
          <SubLeaseFilters
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            isFilterOpen={isFilterOpen}
            onFilterToggle={() => setIsFilterOpen(!isFilterOpen)}
            filters={filters}
            onFilterChange={handleFilterChange}
            holders={holders}
            showExport={false}
          />
          <div className="text-base font-bold text-gray-700">
            Total No. of Subleases: {subleases?.length || 0}
          </div>
        </div>

        <SubLeaseSortControls
          sortConfig={sortConfig}
          onSort={handleSort}
        />
      </div>
      <div className="mt-4">
        <EnhancedTable
          columns={[
            { 
              id: 'lease_id', 
              header: 'Lease ID', 
              accessor: (row) => row.lease_id,
              width: 'narrow'
            },
            { 
              id: 'lease_holder', 
              header: 'Leaseholder ID', 
              accessor: (row) => row.lease_holder || '-',
              width: 'narrow'
            },
            { 
              id: 'holder_name', 
              header: 'Name', 
              accessor: (row) => (
                <div className="truncate" title={row.holder_name || '-'}>
                  {formatTextWithLinks(row.holder_name || '-')}
                </div>
              ),
              width: 'medium'
            },
            { 
              id: 'start_date', 
              header: 'Lease Start', 
              accessor: (row) => row.lease_start 
                ? format(new Date(row.lease_start), 'dd/MM/yyyy')
                : '-',
              width: 'narrow'
            },
            { 
              id: 'end_date', 
              header: 'Lease End', 
              accessor: (row) => row.lease_end
                ? format(new Date(row.lease_end), 'dd/MM/yyyy')
                : '-',
              width: 'narrow'
            },
            { 
              id: 'lease_method', 
              header: 'Lease Method', 
              accessor: (row) => row.lease_method || '-',
              width: 'narrow'
            },
            { 
              id: 'lease_type', 
              header: 'Lease Type', 
              accessor: (row) => row.lease_type || '-',
              width: 'narrow'
            },
            { 
              id: 'further_term', 
              header: 'Further Term', 
              accessor: (row) => row.lease_furth_term || '-',
              width: 'narrow'
            },
            { 
              id: 'frequency', 
              header: 'Rental Frequency', 
              accessor: (row) => row.lease_rent_freq || '-',
              width: 'narrow'
            },
            { 
              id: 'lease_dealing', 
              header: 'Lease Dealing No.', 
              accessor: (row) => row.lease_dealing || '-',
              width: 'medium'
            },
            { 
              id: 'deal_doc', 
              header: 'Lease Docs', 
              accessor: (row) => (
                <ExpandableText 
                  text={row.lease_deal_doc || 'Not specified'} 
                  maxLength={50} 
                  className="text-sm"
                />
              ),
              width: 'wide'
            }
          ]}
          data={subleases || []}
          keyField="lease_id"
          width="2800px"
          maxHeight="calc(100vh - 16rem)"
          loading={isLoading}
          emptyMessage="No subleases found"
          horizontalScroll={true}
        />
      </div>
    </div>
  );
}
